import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Point } from "@turf/helpers";
import { EvaSource, TimeStep } from "constants/charts.enums";
import { Subject } from "rxjs";

import { SystemStatusResponse } from "api/systemStatus";

import { ICategoryFieldModel } from "models";
import { ArpSegment, ForecastConstant, WellData } from "models/UserArpsModel";
import { PdenDataSourceSetting, PdenSourceEnum } from "models/pdenDataSourceSetting";

import { buildColorLockKey } from "components/filter/util/colorLockKey";
import { ForecastWell, OnTime } from "components/forecasting/models/forecastWell";

import { Stick } from "../../../components/pad-generator/models/scenario";
import { EntityKind } from "../../../models/entityKind";
import { getSessionItem, setSessionItem } from "../../utils/sessionStorageHelper";

export interface ICheckedForecast {
  folderId: string;
  isFolder: boolean;
  id: string;
  type: "folder" | "forecast";
  title: string;
  reserveCategory?: string;
  children?: ICheckedForecast[];
  color?: string;
  thickness?: number;
  wellData?: WellData;
  uniqueID: string;
  folderName: string;
  arps: ArpSegment[];
  constants: ForecastConstant[];
  source?: string;
  onTimeArray?: OnTime[];
}

export interface IUserForecastSettings {
  forecastSourceFolder: string;
  secondaryUserForecastSourceFolder?: string;
  excludeMcDanielForecasts: boolean;
  excludeNoviForecasts: boolean;
  specialForecastFolders: string[];
  reserveCategory: string;
}

export interface SyncWells {
  activeWells: string[];
  centroid: Point;
  showWellsWithin: boolean;
  xMiles: number;
  /**
   * The sync is on/off
   */
  isSync: boolean;
  /**
   * Where the sync request came from
   */
  syncRequestOrigin: "Focus" | "Well Hierarchy";
  usePdenFromClient: boolean;
  useFcstFromClient: boolean;
  forecastRescat: string;
  schema: string;
  highlightSyncedWell?: boolean;
}

export interface ColorLockedLegendItem {
  value: string;
  color: string;
  property: string;
  isPreferred?: boolean;
}

type ShowGeoMapLayer = {
  show: boolean;
  layerId: number;
  mapKind: number;
  field: string;
  min: number;
  max: number;
  unit: string;
};

export enum ForecastMode {
  Trump = "forecastTrump",
  Compare = "forecastCompare"
}

export type ForecastModeT = ForecastMode;
export type PadScenario = {
  scenarioId: string;
  showSticksOnMap: boolean;
  play: string;
  sticks?: Stick[];
};

export interface SystemStatusState {
  currentStatus: Maybe<SystemStatusResponse>;
  lastChecked: Maybe<Date>;
  isPolling: boolean;
  hasActiveIssue: boolean;
}

export interface SignificantFiguresSettings {
  defaultSignificantFigures: number;
  useStrictRounding: boolean;
}

export interface AppState {
  userForecastSettings: IUserForecastSettings;
  syncWells?: SyncWells;
  forecastWell?: ForecastWell;
  newFieldRequestObserver: Subject<CustomFieldPayload>;
  normalizeTypeWell: boolean;
  pdenDataSourceSetting: PdenDataSourceSetting;
  lockedColors: { [propertyValueKey: string]: ColorLockedLegendItem };
  showGeoMapLayer?: ShowGeoMapLayer;
  activeEntityKinds: EntityKind[];
  /**
   * @deprecated Migrated to `useState` in `Home.tsx`. No longer read from Redux.
   * Removal deadline: next release cycle after Phase 4 sign-off.
   */
  activeHeaderKey: string;
  /**
   * @deprecated Migrated to `useState` in `Home.tsx`, passed as props to `ActivityBar`.
   * No longer read from Redux. Removal deadline: next release cycle after Phase 4 sign-off.
   */
  groupedPanels: string[];
  forecastMode: ForecastModeT;
  useForecastStartDate: boolean;
  significantFiguresSettings: SignificantFiguresSettings;
  systemStatus: SystemStatusState;

  //the pad scenario is used to show the sticks on the map
  padScenario?: PadScenario;
  //the checked pad scenarios are used to show multiple scenarios on the map
  checkedPadScenarios: PadScenario[];
}
export enum Mode {
  SingleEntity = 0,
  GroupEntity = 1
}

export type ModeState =
  | { type: Mode.SingleEntity }
  | { type: Mode.GroupEntity; field: string };

const LOCKED_COLOR_KEY = "locked-colors";

/**
 * Re-keys each stored locked-colour entry through `buildColorLockKey`. This
 * migrates entries stored under the legacy data-derived key
 * (e.g. "Property:0 - 0.03 (Q1)") to the stable ordinal key
 * (e.g. "Property:(Q1)").
 */
export function migrateLockedColors(stored: {
  [propertyValueKey: string]: ColorLockedLegendItem;
}): { [propertyValueKey: string]: ColorLockedLegendItem } {
  const migrated: { [propertyValueKey: string]: ColorLockedLegendItem } = {};
  Object.values(stored ?? {}).forEach((entry) => {
    // Type-checked, not just null-checked: this input is JSON.parse'd sessionStorage, and
    // getSessionItem's try/catch covers only the parse, not what comes out of it.
    if (typeof entry?.property !== "string" || typeof entry?.value !== "string") return;
    // Last-write-wins when two legacy drifted ranges collapse to the same ordinal
    // (e.g. "0 - 0.03 (Q1)" and "0 - 0.05 (Q1)" both -> "prop:(Q1)"): acceptable,
    // they are the same quantile position. `.value` is intentionally left as the
    // original (possibly drifted) label; all reads key through buildColorLockKey.
    migrated[buildColorLockKey(entry.property, entry.value)] = entry;
  });
  return migrated;
}

function loadLockedColorsFromSession(): {
  [propertyValueKey: string]: ColorLockedLegendItem;
} {
  return migrateLockedColors(
    getSessionItem<{
      [propertyValueKey: string]: ColorLockedLegendItem;
    }>(LOCKED_COLOR_KEY, {})
  );
}

const getDefaultActiveEntityKinds = (): EntityKind[] => {
  return getSessionItem<EntityKind[]>("activeEntityKinds", [EntityKind.Well]);
};

const getDefaultPdenSource = (): EvaSource => {
  const groupBy = getSessionItem<{ pdenSource?: PdenSourceEnum } | null>(
    "globalGroupBy",
    null
  );
  if (!groupBy) return EvaSource.Public;

  switch (groupBy.pdenSource) {
    case PdenSourceEnum.Private:
      return EvaSource.Private;
    case PdenSourceEnum.Hybrid:
      return EvaSource.Hybrid;
    case PdenSourceEnum.Public:
      return EvaSource.Public;
    default:
      return EvaSource.Public;
  }
};
const initialState: AppState = {
  lockedColors: loadLockedColorsFromSession(),

  userForecastSettings: {
    forecastSourceFolder: null,
    secondaryUserForecastSourceFolder: null,
    excludeMcDanielForecasts: false,
    excludeNoviForecasts: true,
    reserveCategory: null,
    specialForecastFolders: []
  },
  newFieldRequestObserver: new Subject(),
  syncWells: null,
  forecastWell: null,
  normalizeTypeWell: true,
  pdenDataSourceSetting: {
    source: getDefaultPdenSource(),
    timeStep: TimeStep.Month
  },
  activeEntityKinds: getDefaultActiveEntityKinds(),
  activeHeaderKey: "groupby-filter",
  groupedPanels: [],
  forecastMode: ForecastMode.Trump,
  significantFiguresSettings: {
    defaultSignificantFigures: 3,
    useStrictRounding: false
  },
  useForecastStartDate: false,
  systemStatus: {
    currentStatus: null,
    lastChecked: null,
    isPolling: false,
    hasActiveIssue: false
  },
  checkedPadScenarios: []
};

export type CustomFieldType = "Calculated" | "Excel";

export interface CustomFieldPayload {
  type: CustomFieldType;
  isOrg: boolean;
  field?: ICategoryFieldModel;
  isEdit?: boolean;
  entityKind?: EntityKind;
  projectId?: string;
  fieldGroup?: string;
  categoryName?: string;
}
const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setDataSourceSetting(state, action: PayloadAction<PdenDataSourceSetting>) {
      state.pdenDataSourceSetting = action.payload;
    },
    setSyncWells(state, action: PayloadAction<SyncWells>) {
      state.syncWells = action.payload;
    },
    setForecastWell(state, action: PayloadAction<Maybe<ForecastWell>>) {
      // Deep copy the forecast well data to prevent Redux immutability issues
      // when utility functions mutate the data
      state.forecastWell = action.payload
        ? JSON.parse(JSON.stringify(action.payload))
        : null;
    },
    setNormalizeTypeWell(state, action: PayloadAction<boolean>) {
      state.normalizeTypeWell = action.payload;
    },
    setUserForecastSettings(state, action: PayloadAction<IUserForecastSettings>) {
      state.userForecastSettings = action.payload;
    },
    ///requests to add a new field, true is org fields, false is user fields
    requestAddNewField(state, action: PayloadAction<CustomFieldPayload>) {
      state.newFieldRequestObserver.next(action.payload);
    },
    addToLockedColor(state, action: PayloadAction<ColorLockedLegendItem>) {
      const key = buildColorLockKey(action.payload.property, action.payload.value);
      const clone = { ...state.lockedColors };
      clone[key] = action.payload;
      state.lockedColors = clone;
      setSessionItem(LOCKED_COLOR_KEY, state.lockedColors);
    },
    removeLockedColor(state, action: PayloadAction<ColorLockedLegendItem>) {
      const key = buildColorLockKey(action.payload.property, action.payload.value);
      if (state.lockedColors[key]) {
        delete state.lockedColors[key];
      }
      setSessionItem(LOCKED_COLOR_KEY, state.lockedColors);
    },
    setShowGeoMapLayer(state, action: PayloadAction<ShowGeoMapLayer>) {
      state.showGeoMapLayer = action.payload;
    },
    setActiveEntities(state, action: PayloadAction<EntityKind[]>) {
      state.activeEntityKinds = action.payload;
      setSessionItem("activeEntityKinds", action.payload);
    },
    /**
     * @deprecated Active header state has been migrated to `useState` in `Home.tsx`.
     * Do not dispatch this action from new code.
     * Removal deadline: next release cycle after Phase 4 sign-off.
     */
    setActiveHeaderKey(state, action: PayloadAction<string>) {
      state.activeHeaderKey = action.payload;
    },
    /**
     * @deprecated Grouped panels state has been migrated to `useState` in `Home.tsx`
     * and passed as props to `ActivityBar`. Do not dispatch this action from new code.
     * Removal deadline: next release cycle after Phase 4 sign-off.
     */
    setGroupedPanels(state, action: PayloadAction<string[]>) {
      state.groupedPanels = action.payload;
    },
    setForecastMode(state, action: PayloadAction<ForecastModeT>) {
      state.forecastMode = action.payload;
    },
    setPadScenario(
      state,
      action: PayloadAction<{
        scenarioId: string;
        showSticksOnMap: boolean;
        play: string;
        sticks?: Stick[];
      }>
    ) {
      state.padScenario = action.payload;
    },
    setCheckedPadScenarios(state, action: PayloadAction<PadScenario[]>) {
      state.checkedPadScenarios = action.payload;
    },
    setUseForecastStartDate(state, action: PayloadAction<boolean>) {
      state.useForecastStartDate = action.payload;
    },
    setSignificantFiguresSettings(
      state,
      action: PayloadAction<SignificantFiguresSettings>
    ) {
      state.significantFiguresSettings = action.payload;
    },
    setSystemStatus(state, action: PayloadAction<Maybe<SystemStatusResponse>>) {
      state.systemStatus.currentStatus = action.payload;
      state.systemStatus.hasActiveIssue = action.payload !== null;
      state.systemStatus.lastChecked = new Date();
    },
    setSystemStatusPolling(state, action: PayloadAction<boolean>) {
      state.systemStatus.isPolling = action.payload;
    },
    resetAppSlice: () => initialState
  }
});

export default appSlice.reducer;
export const {
  setUserForecastSettings,
  setSyncWells,
  setForecastWell,
  requestAddNewField,
  setNormalizeTypeWell,
  setDataSourceSetting,
  addToLockedColor,
  removeLockedColor,
  setPadScenario,
  setCheckedPadScenarios,
  setActiveEntities,
  /** @deprecated Use `useState` in `Home.tsx` instead. */
  setActiveHeaderKey,
  /** @deprecated Use `useState` in `Home.tsx` and pass as props to `ActivityBar` instead. */
  setGroupedPanels,
  setForecastMode,
  setUseForecastStartDate,
  setSignificantFiguresSettings,
  setSystemStatus,
  setSystemStatusPolling,
  resetAppSlice
} = appSlice.actions;
